import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGetProjectMapSVG, useGetProjectVersionChanges } from 'src/api/project'
import { useGetProjectPositions } from 'src/api/projectPosition'
import { IPositionType, IPositionWithDrawing } from 'src/generated-types'
import { IOlexMatchPreview } from 'src/generated-types/olex-match'
import { IProjectInfo } from 'src/generated-types/project'
import { IPositionChange, IProjectVersionChanges } from 'src/generated-types/project-version-changes'
import { OlexRequest } from './olexMatch.action'
import { indexChanges } from './projectVersionChanges.action'
import { isDraftVersion } from './projectVersion.action'

export type RevisionMapView = 'after' | 'before'
export type RevisionTool = 'select' | 'addLine' | 'remove'
// What the dialog's workspace shows: the positions, or the grid expansion or Olex upload in place of them.
export type RevisionMode = 'configure' | 'expand' | 'olex'

export interface RevisionConfigureOptions {
  // An Olex file the project setup already matched to the draft; it opens the Olex mode.
  olexRequest?: OlexRequest
  onOlexApplied?: (preview: IOlexMatchPreview) => void
}

// Revision state of the Configure Positions dialog; only exists while a draft is open.
export interface RevisionConfigure {
  project: IProjectInfo
  changes?: IProjectVersionChanges
  changeByName: Map<string, IPositionChange>
  // Positions the draft removed, by their published name.
  removedByName: Map<string, IPositionChange>
  baseSvg?: string
  basePositions: IPositionWithDrawing[]
  highlight: boolean
  setHighlight: (value: boolean) => void
  // "before" shows the published map as it was.
  view: RevisionMapView
  setView: (view: RevisionMapView) => void
  // What a click on the map does: select, add a mooring line from a corner, or remove a position.
  tool: RevisionTool
  setTool: (tool: RevisionTool) => void
  mode: RevisionMode
  // Entering a mode shows the draft as it is now, since expansions and uploads only apply to it.
  setMode: (mode: RevisionMode) => void
  // The file handed over by the project setup, until the Olex mode has taken it.
  olexRequest?: OlexRequest
  consumeOlexRequest: () => void
  onOlexApplied?: (preview: IOlexMatchPreview) => void
  // Names of removed positions picked on the map (their ghosts); they can be restored from the selection panel.
  selectedRemoved: string[]
  toggleRemoved: (name: string) => void
  setSelectedRemoved: (names: string[]) => void
}

export const useRevisionConfigure = (
  project: IProjectInfo,
  { olexRequest, onOlexApplied }: RevisionConfigureOptions = {},
): RevisionConfigure | undefined => {
  const isRevision = isDraftVersion(project)
  const [highlight, setHighlight] = useState(true)
  const [view, setView] = useState<RevisionMapView>('after')
  const [tool, setTool] = useState<RevisionTool>('select')
  const [mode, setModeState] = useState<RevisionMode>('configure')
  const [handledRequest, setHandledRequest] = useState<OlexRequest>()
  const pendingRequest = olexRequest !== handledRequest ? olexRequest : undefined
  const consumeOlexRequest = useCallback(() => setHandledRequest(olexRequest), [olexRequest])
  const setMode = useCallback((next: RevisionMode) => {
    setModeState(next)
    if (next !== 'configure') setView('after')
  }, [])
  useEffect(() => {
    if (pendingRequest) setMode('olex')
  }, [pendingRequest, setMode])
  const [selectedRemoved, setSelectedRemoved] = useState<string[]>([])
  const toggleRemoved = useCallback(
    (name: string) =>
      setSelectedRemoved(names => (names.includes(name) ? names.filter(n => n !== name) : [...names, name])),
    [],
  )
  const { data: changes } = useGetProjectVersionChanges(project.customerId, project.localityId, project.id, isRevision)
  const baseProjectId = changes?.baseProjectId as EntityId
  // The map hook ignores its flag and always fetches, so it points at this draft's own (cached) map until the base is known.
  const { data: baseSvgData } = useGetProjectMapSVG(
    project.customerId,
    project.localityId,
    changes?.baseProjectId ?? project.id,
    !!changes,
  )
  const baseSvg = changes ? baseSvgData : undefined
  const { data: basePositions } = useGetProjectPositions(project.customerId, baseProjectId)
  const { changeByName, removedByName } = useMemo(() => indexChanges(changes), [changes])

  return useMemo(
    () =>
      isRevision
        ? {
            project,
            changes,
            changeByName,
            removedByName,
            baseSvg,
            basePositions: basePositions ?? [],
            highlight,
            setHighlight,
            view,
            setView,
            tool,
            setTool,
            mode,
            setMode,
            olexRequest: pendingRequest,
            consumeOlexRequest,
            onOlexApplied,
            selectedRemoved,
            toggleRemoved,
            setSelectedRemoved,
          }
        : undefined,
    [
      isRevision,
      project,
      changes,
      changeByName,
      removedByName,
      baseSvg,
      basePositions,
      highlight,
      view,
      tool,
      mode,
      setMode,
      pendingRequest,
      consumeOlexRequest,
      onOlexApplied,
      selectedRemoved,
      toggleRemoved,
    ],
  )
}

// Add line works on the mooring line tab: picking the tool opens that tab, and leaving the tab puts the tool back to Select.
export const useRevisionToolType = (
  revision: RevisionConfigure | undefined,
  activeType: IPositionType | null,
  availableTypes: IPositionType[],
  switchType: (type: IPositionType) => void,
) => {
  const tool = revision?.tool
  const setTool = revision?.setTool
  const previousTool = useRef(tool)
  useEffect(() => {
    const toolChanged = previousTool.current !== tool
    previousTool.current = tool
    if (tool !== 'addLine' || activeType === IPositionType.MooringLine) return
    if (toolChanged && availableTypes.includes(IPositionType.MooringLine)) switchType(IPositionType.MooringLine)
    else setTool?.('select')
  }, [tool, setTool, activeType, availableTypes, switchType])
}
